<script lang="ts">
	import {
		ActionsContainer,
		AdvancedCard,
		Button,
		IconButton,
		Modal,
		Select,
		Stack,
	} from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	// Components
	import EditProfileName from './EditProfileName.svelte';
	// Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	// Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	// Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { beforeUpdate } from 'svelte';

	export let editableObject;

	$: profileOptions = Object.values(editableObject.profiles).map((p) => p.name);

	let cacheActiveProfile = editableObject.activeProfile;

	let addNewProfile = false;
	let editProfile = false;

	const handleProfileChange = async (e) => {
		Object.values(editableObject.profiles).map((profile) => {
			profile.name === e.detail ? (editableObject.activeProfile = profile.id) : 0;
		});
		editableObject.profileName = e.detail;
		cacheActiveProfile = editableObject.activeProfile;

		/**
		 * /rpc/Thermostat.Schedule.SetConfig?config={"id":0,"profile_id":0}
		 */
		const res: SettingRequest = await gen2Request(
			editableObject.device,
			'Thermostat.Schedule.SetConfig',
			{
				config: {
					id: 0,
					profile_id: editableObject.activeProfile,
				},
			},
		);
		onSettingsResponse(res, editableObject.device, {
			success: $format('sched_profile_change'),
		});
	};

	const toggleNewProfileModal = () => {
		editableObject.profileName = '';
		addNewProfile = true;
	};

	const toggleEdit = () => {
		editProfile = true;
	};

	const handleNameSave = async () => {
		editableObject.loading = true;
		const res: SettingRequest = await gen2Request(
			editableObject.device,
			'Thermostat.Schedule.RenameProfile',
			{
				id: 0,
				profile_id: Number(editableObject.activeProfile),
				name: editableObject.profileName,
			},
		);
		// const response = onSettingsResponse(res, editableObject.device);
		if (res.isok) {
			profileOptions[editableObject.activeProfile] = editableObject.profileName;
			editableObject.profiles[editableObject.activeProfile].name = editableObject.profileName;
			// editableObject.activeProfile = editableObject.activeProfile;
		} else {
			closeNewProfileModal();
		}
		editProfile = false;
		editableObject.loading = false;
	};

	const handleAddNewProfile = async () => {
		editableObject.loading = true;
		const res: SettingRequest = await gen2Request(
			editableObject.device,
			'Thermostat.Schedule.AddProfile',
			{
				id: 0,
				name: editableObject.profileName,
			},
		);
		// const response = onSettingsResponse(res, editableObject.device);
		if (res.isok) {
			editableObject.activeProfile = res.data?.profile_id;
			editableObject.profiles[res?.data?.profile_id] = {
				name: editableObject.profileName,
				id: res?.data?.profile_id,
			};
		} else {
			onSettingsResponse(res, editableObject.device, {
				danger: res?.errors?.message,
			});
			closeNewProfileModal();
		}
		addNewProfile = false;
		editableObject.loading = false;
	};

	const closeNewProfileModal = () => {
		editableObject.profileName = editableObject.profiles[editableObject.activeProfile].name;
		addNewProfile = false;
	};

	const handleDeleteProfile = async () => {
		editableObject.loading = true;
		let index;
		const profiles = editableObject.profiles;
		const _profiles = Object.values(editableObject.profiles);
		_profiles.map((p, i) => {
			if (p.id === editableObject.activeProfile) {
				index = i;
			}
		});
		_profiles.splice(index, 1);
		const res: SettingRequest = await gen2Request(
			editableObject.device,
			'Thermostat.Schedule.DeleteProfile',
			{
				id: 0,
				profile_id: editableObject.activeProfile,
			},
		);
		onSettingsResponse(res, editableObject.device);
		_profiles.forEach((profile) => {
			delete profiles[index];
			profiles[profile.id] = profile;
		});
		editableObject.activeProfile = Object.values(profiles)[0].id; //going to default profile which id isn't 0
		editableObject.profileName = editableObject.profiles[editableObject.activeProfile].name;
		editableObject.profiles = profiles;
		editableObject.loading = false;
	};

	beforeUpdate(() => {
		if (cacheActiveProfile !== editableObject.activeProfile) {
			handleProfileChange({ detail: editableObject.profileName });
		}
	});
</script>

<AdvancedCard
	class="mb-s d-flex align-items-center"
	title={$format('title_thermostat_profile')}
	hideSlotTitle={false}
>
	<Select
		class="mt-s"
		icon={'fas fa-user'}
		options={profileOptions}
		value={editableObject.profileName}
		on:change={handleProfileChange}
		disabled={editableObject.loading}
		slot="main"
		id="select-emvpyg6"
		aria-label={$format('title_thermostat_profile', {
			default: 'Thermostat Profile',
		})}
	/>
	<ActionsContainer orientation="horizontal" type="simple" slot="right" class="mt-xl mb-s">
		<IconButton
			icon="fas fa-pen"
			on:click={toggleEdit}
			slot="first"
			loading={editableObject.loading}
			aria-label={$format('button_edit', { default: 'Edit' })}
		/>
		<IconButton
			disabled={editableObject.loading || editableObject.activeProfile === 0}
			icon="fas fa-trash"
			on:click={handleDeleteProfile}
			slot="second"
			variant="danger"
			aria-label={$format('button_delete', { default: 'Delete' })}
		/>
	</ActionsContainer>
</AdvancedCard>
<Button
	disabled={Object.keys(editableObject.profiles).length === 5 || editableObject.loading}
	width="full"
	size="m"
	class="mb-s"
	icon="fas fa-plus"
	on:click={toggleNewProfileModal}
	variant="secondary">{$format('button_add_profile', { default: 'Add profile' })}</Button
>

{#if editProfile}
	<!-- For UX/UI discussion -->
	<Modal
		bind:showModal={editProfile}
		title={$format('title_edit_profile_name', { default: 'Edit profile name' })}
		on:close={() => (editProfile = false)}
		removeScrollPadding
	>
		<EditProfileName {editableObject} slot="content" />
		<div slot="footer" class="d-flex justify-content-end">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (editProfile = false)}
			>
				{$format('button_cancel')}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={handleNameSave}
			>
				{$format('button_save')}
			</Button>
		</div>
	</Modal>
{/if}

{#if addNewProfile}
	<!-- For UX/UI discussion -->
	<Modal
		bind:showModal={addNewProfile}
		title={$format('title_add_new_profile', { default: 'Add new profile' })}
		on:close={closeNewProfileModal}
		removeScrollPadding
	>
		<EditProfileName {editableObject} slot="content" />
		<div slot="footer" class="d-flex justify-content-end">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={closeNewProfileModal}
			>
				{$format('button_cancel')}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={handleAddNewProfile}
			>
				{$format('button_save')}
			</Button>
		</div>
	</Modal>
{/if}
